// standards-ignore: prefer-sdk-enums — this module WRITES `responseType` and
// `showWhenParentIs` back onto the local `SubQuestion` type, whose fields are
// typed as the local `enum ResponseType` / `enum ShowOn` (src/types/helios/
// Question.ts). TS enums are nominal, so the SDK's `ResponseType` string-union
// value is NOT assignable to that field — only the local enum is. (The shipped
// perform-audit code reads with the SDK enum because it never assigns back onto
// a local `SubQuestion`; a write path cannot.) Values are identical across the
// two enum families.
import { ResponseType, ShowOn, type SubQuestion } from '../../../types/helios/Question';
import { isDataCollectionType } from './responseTypeClassification';

/**
 * Enhanced Response Types (ERT) authoring logic — the single source of
 * truth for the per-`responseType` FIELD SHAPE of a sub-question and the
 * conditional-display rules, so neither the sub-question row UI nor the
 * parent step hand-rolls (and drifts on) these rules.
 *
 * Every rule here is a direct port of the manual (v1 Cloudscape) flow's
 * `SubQuestionSidePanel.handleSave` cleanup and `SubQuestions`
 * `handleConditionalToggleChange` guard — but as pure, unit-tested logic
 * instead of inline component code. Pure — no react, no side effects.
 */

/** The default response type for a fresh or agent-seeded sub-question:
 *  Pass/Fail (scored). Astra returns only P/F for P0; the user opts into
 *  other types. */
export const DEFAULT_RESPONSE_TYPE = ResponseType.PASS_FAIL;

/**
 * Response types whose answer is chosen from an admin-defined option list:
 * SINGLE_SELECT and MULTI_SELECT. YES_NO is also option-backed but its
 * options are auto-populated (`['Yes','No']`), never author-edited — see
 * {@link autoOptionsFor}.
 */
export function isEditableOptionType(type: ResponseType | undefined): boolean {
  return type === ResponseType.SINGLE_SELECT || type === ResponseType.MULTI_SELECT;
}

/**
 * Any type that stores an `options` array at all: the two editable select
 * types plus YES_NO (whose options are fixed). Used to decide whether the
 * `options` field survives a type change.
 */
export function isOptionBackedType(type: ResponseType | undefined): boolean {
  return isEditableOptionType(type) || type === ResponseType.YES_NO;
}

/** The fixed options for YES_NO; `undefined` for every other type (editable
 *  select types carry author-entered options, not auto ones). Module-level
 *  constant kept off the hot path for reference stability. */
const YES_NO_OPTIONS = ['Yes', 'No'] as const;

export function autoOptionsFor(type: ResponseType): readonly string[] | undefined {
  return type === ResponseType.YES_NO ? YES_NO_OPTIONS : undefined;
}

/**
 * Returns a clean {@link SubQuestion} after its `responseType` changes to
 * `nextType`, dropping every field that no longer applies so a stale flag
 * from the previous type never rides along into the save payload:
 *
 *   - DATA-COLLECTION type → drop the four scored P/F requirement flags
 *     (`require{Comment,Evidence}On{Fail,Pass}`); keep/allow the
 *     unconditional `requireComment` / `requireAttachment`.
 *   - PASS_FAIL → drop the unconditional flags AND `options` AND
 *     `showWhenParentIs` (a scored sub can never be conditional — see
 *     {@link canEnableConditional}).
 *   - YES_NO → set `options` to the fixed `['Yes','No']`.
 *   - editable select type → keep any existing author options (the caller's
 *     options editor owns their content); DON'T fabricate any here.
 *   - any non-option-backed type → drop `options`.
 *
 * Mirrors v1 `handleSave`'s stale-field cleanup. Returns a NEW object; never
 * mutates the input. Absent keys (not `undefined` values) keep the result
 * clean under exactOptionalPropertyTypes.
 */
export function normalizeSubForResponseType(
  sub: SubQuestion,
  nextType: ResponseType,
): SubQuestion {
  // Start from the sub minus every ERT-managed field; re-add only what the
  // next type keeps. Rebuilding via entries (rather than delete) keeps the
  // result free of `undefined`-valued keys.
  const MANAGED_KEYS = new Set<string>([
    'responseType',
    'options',
    'showWhenParentIs',
    'requireComment',
    'requireAttachment',
    'requireCommentOnFail',
    'requireEvidenceOnFail',
    'requireCommentOnPass',
    'requireEvidenceOnPass',
  ]);
  const base = Object.fromEntries(
    Object.entries(sub).filter(([key]) => !MANAGED_KEYS.has(key)),
  ) as SubQuestion;

  const next: SubQuestion = { ...base, responseType: nextType };

  if (isDataCollectionType(nextType)) {
    // Carry forward the unconditional flags (default off) — the row's
    // toggles own their values.
    next.requireComment = sub.requireComment ?? false;
    next.requireAttachment = sub.requireAttachment ?? false;
    const auto = autoOptionsFor(nextType);
    if (auto) {
      next.options = [...auto];
    } else if (isEditableOptionType(nextType)) {
      // Keep author-entered options across an editable→editable switch.
      next.options = sub.options ? [...sub.options] : [];
    }
    // Preserve an existing conditional trigger; the parent toggle owns
    // whether it's meaningful.
    if (sub.showWhenParentIs) next.showWhenParentIs = sub.showWhenParentIs;
  } else {
    // PASS_FAIL: carry forward the four scored flags (default off); drop
    // everything data-collection / conditional.
    next.requireCommentOnFail = sub.requireCommentOnFail ?? false;
    next.requireEvidenceOnFail = sub.requireEvidenceOnFail ?? false;
    next.requireCommentOnPass = sub.requireCommentOnPass ?? false;
    next.requireEvidenceOnPass = sub.requireEvidenceOnPass ?? false;
  }

  return next;
}

/**
 * Whether a sub-question is the scored Pass/Fail kind (absent or
 * `PASS_FAIL` responseType). The negation of {@link isDataCollectionType},
 * spelled out for readable call sites in the conditional guard.
 */
export function isPassFailSub(sub: SubQuestion): boolean {
  return !isDataCollectionType(sub.responseType);
}

/**
 * The v1 rule: conditional display can only be enabled when EVERY
 * sub-question is data-collection. Conditional visibility is gated on the
 * PARENT's Pass/Fail result, so a Pass/Fail sub can't itself be conditional
 * (it would gate on its own result). Returns false the moment any sub is
 * scored P/F. An empty list can enable conditional (nothing blocks it).
 */
export function canEnableConditional(subs: readonly SubQuestion[]): boolean {
  return subs.every((sub) => !isPassFailSub(sub));
}

/**
 * Applies the parent conditional toggle to the sub list:
 *
 *   - enabling → every sub without a `showWhenParentIs` defaults to BOTH
 *     (matches v1: unset subs appear for both Pass and Fail);
 *   - disabling → `showWhenParentIs` is stripped from every sub (they then
 *     always appear, regardless of parent result).
 *
 * Returns a NEW array of NEW sub objects; never mutates. Callers should gate
 * enabling behind {@link canEnableConditional} first.
 */
export function applyConditionalDefaults(
  subs: readonly SubQuestion[],
  enabled: boolean,
): SubQuestion[] {
  if (enabled) {
    return subs.map((sub) =>
      sub.showWhenParentIs ? sub : { ...sub, showWhenParentIs: ShowOn.BOTH },
    );
  }
  return subs.map((sub) => {
    if (!sub.showWhenParentIs) return sub;
    const entries = Object.entries(sub).filter(([key]) => key !== 'showWhenParentIs');
    return Object.fromEntries(entries) as unknown as SubQuestion;
  });
}

/**
 * The message a conditional-toggle change should surface to the author:
 *   - 'blocked'   → the toggle could NOT be enabled (a Pass/Fail sub exists);
 *   - 'defaulted' → enabled, and some subs had no trigger so were set to BOTH;
 *   - 'enabled'   → enabled, every sub already had a trigger (no defaulting);
 *   - 'disabled'  → turned off;
 *   - null        → no message (e.g. enabling with no sub-questions at all).
 */
export type ConditionalToggleMessage = 'blocked' | 'defaulted' | 'enabled' | 'disabled';

/**
 * The outcome of flipping the parent "conditional sub-questions" toggle:
 * whether the change is ALLOWED, the resulting `enabled` flag to persist, the
 * next sub-question list (defaults applied / trigger stripped), and which
 * message to surface. Pure decision — the UI renders `message` and persists
 * `enabled` + `subs` only when `allowed`.
 */
export interface ConditionalToggleOutcome {
  readonly allowed: boolean;
  readonly enabled: boolean;
  readonly subs: readonly SubQuestion[];
  readonly message: ConditionalToggleMessage | null;
}

/**
 * Decides what happens when the author flips the conditional-display toggle to
 * `nextEnabled`, encoding the v1 rule (SubQuestions.handleConditionalToggleChange):
 *
 *   - Enabling while any Pass/Fail sub exists → BLOCKED: not allowed, state
 *     unchanged, 'blocked' message ({@link canEnableConditional} is false).
 *   - Enabling otherwise → allowed; subs get {@link applyConditionalDefaults};
 *     message is 'defaulted' when at least one sub had no prior trigger, else
 *     'enabled', or null when there are no subs to speak of.
 *   - Disabling → allowed; `showWhenParentIs` stripped from every sub;
 *     'disabled' message when there were subs, else null.
 *
 * Pure — no react, no side effects. The step calls this, then (when allowed)
 * emits the new configuration flag + subs and shows the message.
 */
export function resolveConditionalToggle(
  subs: readonly SubQuestion[],
  nextEnabled: boolean,
): ConditionalToggleOutcome {
  if (nextEnabled) {
    if (!canEnableConditional(subs)) {
      return { allowed: false, enabled: false, subs, message: 'blocked' };
    }
    const hadUnset = subs.some((sub) => !sub.showWhenParentIs);
    const nextSubs = applyConditionalDefaults(subs, true);
    const message: ConditionalToggleMessage | null =
      subs.length === 0 ? null : hadUnset ? 'defaulted' : 'enabled';
    return { allowed: true, enabled: true, subs: nextSubs, message };
  }
  const nextSubs = applyConditionalDefaults(subs, false);
  return {
    allowed: true,
    enabled: false,
    subs: nextSubs,
    message: subs.length === 0 ? null : 'disabled',
  };
}
